'use client';

import { createContext, useContext, useEffect } from 'react';

/**
 * The viewer's course-purchase ownership, keyed by a course's derived
 * `listingId` — resolved LAZILY and DEDUPED by {@link CoursePurchaseProvider}
 * as surfaces ask for it, then shared with the unlock banner + every clip
 * lock-overlay so a purchaser sees no locks (on the course page AND in every
 * off-course bio rail). Mirrors PopshopRN's global course-order `memory-cache`
 * (uid + listingId keyed), which unlocks a purchased course everywhere.
 *
 * A single provider spans the whole bio (mounted at the profile layout), so a
 * clip cell in a My Life rail resolves ownership of its parent course by
 * `listingId` without a per-surface provider.
 */
export interface CoursePurchaseRegistry {
  /**
   * Register a course's derived `listingId` so the provider resolves its
   * ownership (idempotent + deduped). A `undefined` id is a no-op. Called from
   * an effect (never during render), so registering never mutates state
   * mid-render.
   */
  register: (listingId: string | undefined) => void;
  /**
   * Ownership of the course backed by `listingId`:
   * - `undefined` → not yet resolved (registered, request in flight); callers
   *   treat this as "still resolving", keeping the lock to avoid a flash.
   * - `true` / `false` → the settled answer.
   */
  ownership: (listingId: string | undefined) => boolean | undefined;
}

/**
 * Default registry — registering is a no-op and everything resolves to `false`
 * (settled, not owned). Used when a surface renders OUTSIDE a
 * {@link CoursePurchaseProvider} (e.g. a Storybook cell), so it stays
 * subscription-only with no request.
 */
export const CoursePurchaseContext = createContext<CoursePurchaseRegistry>({
  register: () => undefined,
  ownership: () => false,
});

export function useCoursePurchaseRegistry(): CoursePurchaseRegistry {
  return useContext(CoursePurchaseContext);
}

/**
 * Register a course `listingId` and read its ownership. Returns
 * `{ purchased, resolved }` — `resolved` is `false` while the answer is still
 * `undefined` so a consumer can hold (keep the lock / defer the banner) to
 * avoid a flash. Registration happens in an effect, so it's render-safe.
 */
export function useCoursePurchased(listingId?: string): {
  purchased: boolean;
  resolved: boolean;
} {
  const { register, ownership } = useCoursePurchaseRegistry();
  useEffect(() => {
    register(listingId);
  }, [register, listingId]);
  const state = ownership(listingId);
  return { purchased: state === true, resolved: state !== undefined };
}
